import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import DetailAuxiliaryDataWrapper from './DetailAuxiliaryDataWrapper';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import {
  hasObjectPermission,
  isAllowedToViewAdminAccess,
  isAllowedToUpdate
} from '../../services/permissions/permissionHelper';

import SaveModal from '../5templates/modal/saveModal/SaveModal';
import CancelModal from '../5templates/modal/cancelModal/CancelModal';
import ErrorModal from '../5templates/modal/errorModal/ErrorModal';

import {
  reExportedColors,
  EditModeButton,
  FeedbackSnackbar,
  Text,
  GoBackButton,
  SaveCancelButtons,
  Loader
} from '../../components/1elements';

import useDetailWrapper from './useDetailWrapper';

const DetailWrapper = ({
  detailEditMode,
  setDetailEditMode,
  typeIdToDisplayName,
  primaryDetailRecord,
  children,
  isLoading,
  secondaryDataLoaderCallBack,
  upsertDetailCallback,
  bulkUpdateCallBack,
  finalAuxiliaryRecords,
  entityDisplayName,
  optionalListenerToFormikValidation,
  extraValidation,
  preserveEditDraft = false
}) => {
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [formikCSSDirty, setFormikCSSDirty] = useState(false);
  const [transientValues, setTransientValues] = useState(null);
  const [errorMessage, setErrorMessage] = useState(
    'Oops, we got an error, we apologize for the inconvenience'
  );

  const {
    isSnackbarOpen,
    setIsSnackbarOpen,
    deleteEntityCallback,
    activationChangeCallback,
    stageActivationChange
  } = useDetailWrapper({
    typeIdToDisplayName,
    primaryDetailRecord,
    finalAuxiliaryRecords,
    bulkUpdateCallBack
  });
  const classes = useStyles({ detailEditMode, isDetailDirty: formikCSSDirty });
  const navigate = useNavigate();
  const createMode = !primaryDetailRecord.id;
  const actualDisplayName = entityDisplayName
    ? entityDisplayName
    : typeIdToDisplayName(primaryDetailRecord.type);
  const isSystem = primaryDetailRecord?.is_system;

  const loadEdit = () => {
    switch (actualDisplayName) {
      case 'Entity':
        return hasObjectPermission(primaryDetailRecord.id, 'update');
      case 'Account':
        return hasObjectPermission(primaryDetailRecord.id, 'update');
      case 'Multi-Tenant Building':
        return hasObjectPermission(primaryDetailRecord.id, 'update');
      case 'Single Tenant Location':
        return hasObjectPermission(primaryDetailRecord.id, 'update');
      case 'User':
        return isAllowedToUpdate('user');
      case 'Contact':
        return isAllowedToUpdate('party');
      case 'Service':
        return isAllowedToUpdate('service');
      case 'Skillset':
        return isAllowedToUpdate('skillset');
      case 'Operational Group':
        return isAllowedToUpdate('group.operational');
      case 'User Group':
        return isAllowedToUpdate('group.user');
      case 'Feature Group':
        return isAllowedToUpdate('group.feature');
      case 'Territory Group':
        return isAllowedToUpdate('group.territory');
      case 'Commercial Zone':
        return isAllowedToUpdate('datazone.commercial');
      case 'Geo Zone':
        return isAllowedToUpdate('datazone.geo');
      default:
        console.warn('Couldnt find type');
        return false;
    }
  };

  const header = createMode
    ? `New ${actualDisplayName}`
    : `${actualDisplayName} Overview`;

  if (isLoading) return <Loader />;

  const saveYes = (resetForm) => () => {
    const { _objectId, ...valueToUpdate } = transientValues
    upsertDetailCallback(valueToUpdate)
      .then((resp) => {
        resetForm();
        setDetailEditMode(false);
        setSaveModalOpen(false);

        if (createMode) {
          navigate(-1);
        } else {
          if (transientValues.refetch) {
            transientValues
              .refetch()
              .then(() => {
                setTransientValues(null);
              })
              // Have to add this because it's async and
              // a raised error will not be cause below.
              // This was happening and we were missing
              // errors
              .catch(console.error);
          }
        }
      })
      .catch((e) => {
        console.log(transientValues);
        console.error(e);
        // if (process.env.NODE_ENV !== 'production') {
        // }
        const rawMessage =
          typeof e === 'string' || e instanceof String ? e : JSON.stringify(e);

        setErrorMessage(rawMessage);
        setErrorModalOpen(true);
        setSaveModalOpen(false);
      });
  };

  const saveNo = () => {
    setTransientValues(null);
    setSaveModalOpen(false);
  };

  return (
    <>
      <Formik
        key={primaryDetailRecord.id || 'new'}
        initialValues={primaryDetailRecord}
        enableReinitialize={!preserveEditDraft || !detailEditMode || createMode}
        validateOnChange={true}
        validate={(values) => {
          setFormikCSSDirty(true);
          if (optionalListenerToFormikValidation) {
            optionalListenerToFormikValidation(values);
          }
          if (extraValidation) {
            return extraValidation(values);
          }
        }}
        onSubmit={(values) => {
          setTransientValues(values);
          setSaveModalOpen(true);
        }}
      >
        {(formikProps) => {
          const discardYes = () => {
            setCancelModalOpen(false);

            if (createMode) {
              navigate(-1);
            } else {
              primaryDetailRecord.refetch && primaryDetailRecord.refetch();
              formikProps.resetForm();
              setDetailEditMode(false);
            }
          };
          const triggerDiscardConfirmation = (val) => {
            // tmp commented to fix saving auxiliary updates
            // if (formikProps.dirty) {
            //   setCancelModalOpen(true);
            // } else {
              if (createMode) {
                navigate(-1);
              } else {
                setDetailEditMode(val);
              }
            // }
          };

          return (
            <div
              className={classes.root}
              style={{ height: '100%', width: '100%' }}
            >
              <div className={classes.header}>
                <Text className={classes.pageHeader}>{header}</Text>
                <div className={classes.headerButtonDiv}>
                  {!isSystem &&
                    !createMode &&
                    (loadEdit() || isAllowedToViewAdminAccess()) && (
                      <EditModeButton
                        onEditMode={detailEditMode}
                        setOnEditMode={triggerDiscardConfirmation}
                      />
                    )}
                  <GoBackButton className={classes.goBackButton} />
                </div>
              </div>
              <Form className={classes.form}>
                <div className={classes.scrollableDiv}>
                  <DetailAuxiliaryDataWrapper
                    secondaryDataLoaderCallBack={secondaryDataLoaderCallBack}
                    secondaryCallbackArgs={primaryDetailRecord}
                  >
                    {children({
                      formikProps,
                      activationChangeCallback,
                      stageActivationChange,
                      deleteEntityCallback
                    })}
                  </DetailAuxiliaryDataWrapper>
                </div>
                {formikProps.dirty && (
                  <div className={classes.buttonDiv}>
                    <div className={classes.headerButtonDiv}>
                      <SaveCancelButtons
                        setOnEditMode={triggerDiscardConfirmation}
                      ></SaveCancelButtons>
                    </div>
                  </div>
                )}
              </Form>
              <SaveModal
                open={saveModalOpen}
                setOpen={setSaveModalOpen}
                confirmFunction={saveYes(formikProps.resetForm)}
                cancelFunction={saveNo}
              >
                Are you sure you want to save this record?
              </SaveModal>
              <ErrorModal open={errorModalOpen} setOpen={setErrorModalOpen}>
                {errorMessage}
              </ErrorModal>
              <CancelModal
                open={cancelModalOpen}
                setOpen={setCancelModalOpen}
                confirmFunction={discardYes}
              >
                Are you sure you want to discard your changes?
              </CancelModal>
            </div>
          );
        }}
      </Formik>

      <FeedbackSnackbar
        snackbar={isSnackbarOpen}
        setSnackbar={setIsSnackbarOpen}
      />
    </>
  );
};

export default DetailWrapper;

const useStyles = makeStyles((theme) => ({
  root: {
    margin: '20px 30px 25px 100px',
    display: 'flex',
    flexDirection: 'column',
    border: `2px solid ${theme.palette.quaternary.accent}`,
    borderRadius: '15px',
    padding: '10px 20px',
    height: 'calc(100vh - 90px)',
    minHeight: 0,
    boxSizing: 'border-box'
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '5px 0 15px',
    flexShrink: 0
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0
  },

  scrollableDiv: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingRight: 10,
    paddingBottom: 20
  },

  buttonDiv: {
    flexShrink: 0,
    padding: '10px 0',
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    opacity: (props) => (props.detailEditMode ? 1 : 0),
    overflow: (props) => (props.detailEditMode ? 'visible' : 'hidden'),
    transition: 'opacity .25s',
    boxShadow: '0px -2px 6px #00000029',
    background: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    boxSizing: 'border-box',

    '&>*': {
      margin: 5
    }
  },

  resetButton: {
    margin: '5px auto 5px 0 !important'
  },

  goBackButton: {
    marginLeft: 20
  },

  pageHeader: {
    fontSize: '22px',
    fontWeight: 'bold',
    fontFamily: 'Lato',
    color: reExportedColors.TEXT
  },

  headerButtonDiv: {
    display: 'flex',
    alignItems: 'center'
  }
}));
